// Tanner Fry
// dev.lasutriv@gmail.com
// Component used for creating a trask for the task tracker.

import '../../css/components/Animations.css';

import { ErrorMessage, Field, Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIPOSTUPDATEUserAnalyticsLoginTrack } from '../../../api/APIUserAnalytics';
import { ApiPostOtpCode, ReturnPropsAPIGetUserLoginResponse, VerifyOTPCodeData } from '../../../api/APIUsers';
import { useAppDispatch, useAppSelector } from '../../../AppHooks';
import { _settings_non_private_routes, getCorrectDomain } from '../../../AppSettings';
import { setLoggedIn, setSubscriptionStatus, setUser } from '../../../slices/UserSlice';
import { SubscriptionPlan } from '../data/SubscriptionData';
import Loading from '../elements/Loading';

interface ILoginVerifyOTPFormProps {
    userPlan: SubscriptionPlan;
    apiLoginData: ReturnPropsAPIGetUserLoginResponse;
    // setUserPlan: React.Dispatch<React.SetStateAction<UserPlan>>
}

function LoginVerifyOTPForm(props: ILoginVerifyOTPFormProps) {  
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const apiLoginData = props.apiLoginData;
    const [errorMessage, setErrorMessage] = useState('');
    const [resendMessage, setResendMessage] = useState('');
    const [isCheckingFormErrorPlacement, setIsCheckingFormErrorPlacement] = useState(false);
    const [isAnimatingLogin, setIsAnimatingLogin] = useState(false);
    const [isAnimatingLoginComplete, setIsAnimatingLoginComplete] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(60);
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const lastLocationWhileLoggedIn = useAppSelector(state => state.users.lastLocation);
    const loginAnimationVariants = {
        swipe: {
            opacity: 1,
            top: "250%",
            transition: {
                duration: 1,
                ease: "easeInOut",
            },
        },
        end: {
            opacity: 1,
            top: "250%"
        },
    }

    const getFriendlyOtpErrorMessage = (rawError?: string) => {
        if (!rawError || rawError.trim() === '') {
            return 'Unable to process OTP right now. Please try again.';
        }

        const lowered = rawError.toLowerCase();
        if (lowered.includes('expired')) {
            return 'That OTP expired. Request a new code and try again.';
        }
        if (lowered.includes('invalid') || lowered.includes('failed')) {
            return 'That OTP is invalid. Please check the code and try again.';
        }
        if (lowered.includes('user not found')) {
            return 'Unable to verify your account. Please log in again.';
        }

        return 'Unable to verify OTP right now. Please try again.';
    };

    useEffect(() => {
        setIsCheckingFormErrorPlacement(true);

        // Guard route access: user must come from successful login with OTP enabled.
        if (!apiLoginData || apiLoginData.error !== '' || !apiLoginData.hasOTPSetup) {
            navigate('/login');
            return;
        }
    }, [apiLoginData, dispatch, navigate, lastLocationWhileLoggedIn])

    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }

        const timer = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [resendCooldown]);

    useEffect(() => {
        // Get form error element and change position
        let formErrorElements = document.getElementsByClassName('form-error');
        if (formErrorElements.length > 0) {
            // Loop
            for (let i = 0; i < formErrorElements.length; i++) {
                // Get form error element's width
                let formErrorElementWidth = formErrorElements[i].clientWidth;
                // Set form error element's left position
                let formErrorStyle = 'left: -' + (10 + formErrorElementWidth) + 'px;';
                // Set form error element's style
                formErrorElements[i].setAttribute('style', formErrorStyle);
            }
            // console.log("formErrorElements: ", formErrorElements);
            
        }

        setIsCheckingFormErrorPlacement(false);
    }, [isCheckingFormErrorPlacement])

    useEffect(() => {
        // If done animating, perform actions after login such as routing user to dashboard or last location
        if (isAnimatingLoginComplete) {
            // Make sure we logged in successfully via APIGetUserLoginResponse
            if ((typeof JSON.parse(localStorage.getItem('user'))?.uuid) == 'string') {
                // TODO: Verify user credentials match server. If not, remove the local credentials
                dispatch(setLoggedIn(true));
            } else {
                dispatch(setLoggedIn(false));
                localStorage.removeItem('user');
                // Check if current route is in _settings_non_private_routes. If not, redirect to login
                if (!_settings_non_private_routes.includes(window.location.pathname)) {
                    navigate('/login');
                }
            }
            
            if (lastLocationWhileLoggedIn === "" && apiLoginData.error === "") {
                // Default navigate to dashboard if no last location is set and no errors
                // console.log("Navigate to /dashboard");
                navigate("/dashboard");
            } else {
                // console.log("Navigate to lastLocationWhileLoggedIn: ", lastLocationWhileLoggedIn);
                // Navigate to user's last location if no errors and they weren't just on the login/register page
                if (lastLocationWhileLoggedIn !== '/login' && lastLocationWhileLoggedIn !== '/register'
                    && apiLoginData.error === "") {
                    navigate(lastLocationWhileLoggedIn);
                } 
            } 
            // console.log("apiLoginData.error: ", apiLoginData.error);

            // Set user plan        
            // props.setUserPlan({...props.userPlan, plan: apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free} as UserPlan);
            dispatch(setSubscriptionStatus(apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free));
        }
    }, [isAnimatingLoginComplete]);

    async function submitForm(values, setSubmitting) {
        const otpCode = [values.otp1, values.otp2, values.otp3, values.otp4, values.otp5, values.otp6].join('');

        // Submit results to db
        const apiOTPData = await ApiPostOtpCode(otpCode);

        if (apiOTPData !== undefined && apiOTPData.error === "") {

            if (apiOTPData.data === VerifyOTPCodeData.APPROVED) {
                dispatch(setSubscriptionStatus(apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free));
                dispatch(setLoggedIn(true));

                // Navigate to dashboard or last location
                if (lastLocationWhileLoggedIn === "" || lastLocationWhileLoggedIn === '/login' || lastLocationWhileLoggedIn === '/register') {
                    navigate("/dashboard");
                } else if (!_settings_non_private_routes.includes(lastLocationWhileLoggedIn)) {
                    navigate(lastLocationWhileLoggedIn);
                } else {
                    navigate("/dashboard");
                }

                setSubmitting(false);
            } else {
                setErrorMessage(getFriendlyOtpErrorMessage(String(apiOTPData.data)));
                setSubmitting(false);
            }
        } else {
            if (apiOTPData === undefined) {
                setErrorMessage('Unable to verify OTP right now. Please try again.');
            } else {
                setErrorMessage(getFriendlyOtpErrorMessage(apiOTPData.error || ''));
            }
            setSubmitting(false);
        }
    }

    function resetForm() {
        
    }

    const focusOtpInput = (index: number) => {
        const input = document.querySelector<HTMLInputElement>(`input[name='otp${index}']`);
        if (input) {
            input.focus();
            input.select();
        }
    };

    async function handleResendOtp() {
        if (resendCooldown > 0 || isResendingOtp) {
            return;
        }

        setIsResendingOtp(true);
        setErrorMessage('');
        setResendMessage('');

        try {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const email = currentUser?.email;

            if (!email) {
                setErrorMessage('Unable to resend OTP: user email not found. Please log in again.');
                setIsResendingOtp(false);
                return;
            }

            const response = await fetch(getCorrectDomain() + '/api/verify/send-email-otp', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });

            const responseData = await response.json();

            if (!response.ok || responseData?.error) {
                console.error('Resend OTP failed:', responseData?.error || 'Unknown resend failure');
                setErrorMessage('Unable to resend OTP right now. Please wait a moment and try again.');
                setIsResendingOtp(false);
                return;
            }

            setResendCooldown(60);
            setResendMessage('A new OTP has been sent to your email.');
        } catch (error) {
            setErrorMessage('Failed to resend OTP. Please try again.');
        } finally {
            setIsResendingOtp(false);
        }
    }

    return (
        <>
            {/* <img className="login-splash-screen" src={require('../../files/splashScreens/Splash-Screen-Farmland.png')} alt="Farmland" /> */}
            <motion.div
                className='form-container-login-register'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            >
                <Formik
                    initialValues={ { otp1: '', otp2: '', otp3: '', otp4: '', otp5: '', otp6: '' } }
                    validate={ values => {
                        setIsCheckingFormErrorPlacement(true);
                        const errors = {
                            otp1: '',
                        };

                        const otpCode = [values.otp1, values.otp2, values.otp3, values.otp4, values.otp5, values.otp6].join('');
                        if (!/^\d{6}$/.test(otpCode)) {
                            errors.otp1 = 'Enter all 6 digits.';
                        }

                        if (/^\d{6}$/.test(otpCode)) {
                            return {};
                        } else {
                            return errors;
                        }
                    }}
                    onSubmit={ (values, { setSubmitting }) => {
                        submitForm(values, setSubmitting);
                    }}
                    onReset={ (values) => {
                        resetForm();
                    }}
                    enableReinitialize={true}
                >
                    { ({ isSubmitting, setFieldValue, values }) => (
                        <>
                            { isSubmitting ? (
                                <>
                                    <Loading withBackground={true} />
                                </>
                            ) : (
                                <>
                                    <Form>
                                        <h2>Verify Login OTP</h2>
                                        <div className='form-group-inputs otp-inputs'>
                                            <div className='otp-container login-otp-container'>
                                                <div className='otp-digit-row'>
                                                    {[1, 2, 3, 4, 5, 6].map((index) => (
                                                        <div key={index} className='field-group'>
                                                            <Field
                                                                type='text'
                                                                as='input'
                                                                name={`otp${index}`}
                                                                maxLength={1}
                                                                autoComplete={index === 1 ? 'one-time-code' : 'off'}
                                                                className='login-otp-digit'
                                                                autoFocus={index === 1}
                                                                onChange={(e) => {
                                                                    const digit = e.target.value.replace(/\D/g, '').slice(-1);
                                                                    setFieldValue(`otp${index}`, digit);

                                                                    if (digit && index < 6) {
                                                                        focusOtpInput(index + 1);
                                                                    }
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Backspace' && !values[`otp${index}`] && index > 1) {
                                                                        setFieldValue(`otp${index - 1}`, '');
                                                                        focusOtpInput(index - 1);
                                                                    }
                                                                }}
                                                                onPaste={(e) => {
                                                                    e.preventDefault();
                                                                    const pastedDigits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

                                                                    if (!pastedDigits) {
                                                                        return;
                                                                    }

                                                                    for (let i = 0; i < 6; i++) {
                                                                        setFieldValue(`otp${i + 1}`, pastedDigits[i] || '');
                                                                    }

                                                                    focusOtpInput(Math.min(pastedDigits.length + 1, 6));
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <ErrorMessage name='otp1' component='div' className='otp-error-message' />
                                            </div>
                                        </div>
                                        <hr />
                                        <div className='button-group'>
                                            <button id='cancel-btn' type='button' onClick={() => navigate('/login')}>Back</button>
                                            <button id='login-btn' type='submit' disabled={ isSubmitting }>Verify</button>
                                            <button
                                                id='reset-btn'
                                                type='button'
                                                disabled={resendCooldown > 0 || isResendingOtp}
                                                onClick={handleResendOtp}
                                            >
                                                {isResendingOtp
                                                    ? 'Resending...'
                                                    : resendCooldown > 0
                                                        ? `Resend OTP (${resendCooldown}s)`
                                                        : 'Resend OTP'}
                                            </button>
                                        </div>
                                        {resendMessage && (
                                            <div className='otp-status-message success'>
                                                {resendMessage}
                                            </div>
                                        )}
                                        {errorMessage && (
                                            <div className='otp-status-message error'>
                                                {errorMessage}
                                            </div>
                                        )}
                                    </Form>
                                </>
                            )}
                        </>
                    )}
                </Formik>
            </motion.div>
        </>
    );
}


export default LoginVerifyOTPForm;